import { useApolloClient, useMutation } from '@apollo/client';
import { REMOVE_CARD } from '@mutations/removeCard';
import { Button, Meta, theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import useCreditCardCount from 'hooks/useCreditCardCount';
import useHasAnprEnabled from 'hooks/useHasAnprEnabled';
import React, { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaymentStore } from 'stores/paymentStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  AdyenWalletType,
  Mutation,
  MutationDeleteCardArgs,
} from 'types/generatedSchemaTypes';
import { isAdyenWalletPaymentMethod } from 'utils/paymentUtils';
import { refreshCustomerBestEffort } from 'utils/refreshCustomerBestEffort';
import { removePaymentCardFromCustomerCache } from 'utils/removePaymentCardFromCustomerCache';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

const ConfirmPaymentMethodRemoval: React.FC = () => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const {
    customer: { paymentCards },
  } = useContext(AccountContext);
  const selectedCard = usePaymentStore((s) => s.selectedPaymentCard);
  const isLastActiveCard =
    useCreditCardCount() === 1 && selectedCard.status === 'Active';
  const hasAnprEnabled = useHasAnprEnabled();
  // Captured at press time so we still clear the local slot after delete
  // even if paymentCards has already changed by the time onCompleted runs.
  const walletTypeToClearRef = useRef<AdyenWalletType | null>(null);

  const [removeCard, { loading }] = useMutation<
    Mutation,
    MutationDeleteCardArgs
  >(REMOVE_CARD, {
    // Customer refresh is best-effort AFTER a successful delete (see
    // refreshCustomerBestEffort) — not via awaitRefetchQueries, which would
    // route a GET_CUSTOMER failure into onError and skip clearing the local
    // wallet slot (still showing "Added" after a successful server delete).
    // Also drop the deleted card from the GET_CUSTOMER cache so occupancy
    // stays free even when that refresh fails.
    variables: {
      cardId: selectedCard?.cardId,
      setAnprFalse: isLastActiveCard && hasAnprEnabled,
    },
    update: (cache, _result, { variables }) => {
      const deletedCardId = variables?.cardId;
      if (deletedCardId) {
        removePaymentCardFromCustomerCache(cache, deletedCardId);
      }
    },
    onCompleted: () => {
      const walletType = walletTypeToClearRef.current;
      walletTypeToClearRef.current = null;
      if (walletType) {
        usePaymentStore.getState().clearAdyenWalletSlot(walletType);
      }
      useUiStore.getState().setSheetModal('');
      showToast('success.paymentCardRemoving', 'success');
      void refreshCustomerBestEffort(client);
    },
    onError: (error) => {
      walletTypeToClearRef.current = null;
      const errorToastMap: Record<string, string> = {
        CANNOT_DELETE_CARD_ONGOING_SESSION:
          'error.paymentCardRemovingOngoingSession',
        CANNOT_DELETE_CARD_ACTIVE_PERMIT:
          'error.paymentCardRemovingActivePermit',
        CANNOT_DELETE_CARD_UNPAID_CHARGING_SESSION:
          'error.paymentCardRemovingUnpaidCharging',
      };
      const matchedCode = error.graphQLErrors.find(
        (e) => errorToastMap[e.extensions?.code as string]
      )?.extensions?.code as string;
      showToast(
        errorToastMap[matchedCode] ?? 'error.paymentCardRemoving',
        'error'
      );
    },
  });

  const handleRemove = () => {
    if (loading) {
      return;
    }
    const paymentMethod = paymentCards?.find(
      (card) => card?.cardId === selectedCard?.cardId
    )?.paymentMethod;
    walletTypeToClearRef.current = isAdyenWalletPaymentMethod(paymentMethod)
      ? paymentMethod
      : null;
    removeCard();
  };

  return (
    <StyledWrapper mb={0}>
      <StyledWrapper mb={20}>
        <StyledWrapper mb={70}>
          <StyledWrapper mb={15}>
            <Meta
              testID="payment-methods-confirm-text"
              center
              color={theme.colors.gray300}>
              {t('paymentMethods.confirmText')}
            </Meta>
          </StyledWrapper>
          {isLastActiveCard && (
            <Meta
              testID="payment-methods-resets-anpr"
              center
              color={theme.colors.gray300}>
              {t('paymentMethods.setAnprFalse')}
            </Meta>
          )}
        </StyledWrapper>
      </StyledWrapper>
      <StyledWrapper mb={0}>
        <Button
          {...testIdentifiers('payment-methods-confirm')}
          size="l"
          text={t('button.remove')}
          loading={loading}
          onPress={handleRemove}
        />
      </StyledWrapper>
    </StyledWrapper>
  );
};

export default ConfirmPaymentMethodRemoval;
